import { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';

// MUI
import {
    CheckCircle,
    School,
    Search,
    Shield,
    TrendingUp,
    Verified,
    VerifiedUser
} from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    Container,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Stack,
    Typography,
    useTheme
} from '@mui/material';

export default function Verification({ apipublic, snackbar }) {
    const navi = useNavigate()
    const theme = useTheme()

    useEffect(() => {
        window.scroll(0, 0)
    }, [])

    const [working, setWorking] = useState('')
    const [enrollment, setEnrollment] = useState('')
    const verify = () => {
        setWorking(true)
        var formData = new FormData()
        formData.append('verify-certificate', enrollment)
        fetch(apipublic, {
            method: 'post',
            body: formData
        }).then(res => res.json())
            .then(data => {
                setWorking(false)
                if (data.status == false) {
                    snackbar(data.message)
                } else {
                    navi(`/verify?enrollment=${enrollment}`)
                }
            })
    }

    return (
        <>
            {/* Hero Section with Search */}
            <Box
                sx={{
                    position: "relative",
                    minHeight: "85vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    overflow: "hidden",
                    pt: { xs: 4, md: 0 },
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: "-50%",
                        right: "-20%",
                        width: "800px",
                        height: "800px",
                        borderRadius: "50%",
                        background: `radial-gradient(circle, ${theme.palette.secondary.main}25 0%, transparent 70%)`,
                        animation: "rotate 30s linear infinite",
                    },
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: "-30%",
                        left: "-10%",
                        width: "600px",
                        height: "600px",
                        borderRadius: "50%",
                        background: `radial-gradient(circle, ${theme.palette.info.main}20 0%, transparent 70%)`,
                        animation: "rotate 25s linear infinite reverse",
                    },
                    "@keyframes rotate": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" }
                    }
                }}
            >
                {/* Floating geometric shapes */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "20%",
                        right: "15%",
                        width: "150px",
                        height: "150px",
                        background: `${theme.palette.secondary.main}15`,
                        borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
                        animation: "morph 8s ease-in-out infinite",
                        "@keyframes morph": {
                            "0%, 100%": { borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%" },
                            "50%": { borderRadius: "70% 30% 30% 70% / 70% 70% 30% 30%" }
                        }
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        bottom: "25%",
                        left: "10%",
                        width: "100px",
                        height: "100px",
                        background: `${theme.palette.info.main}20`,
                        borderRadius: "50%",
                        animation: "float 6s ease-in-out infinite",
                        "@keyframes float": {
                            "0%, 100%": { transform: "translateY(0px)" },
                            "50%": { transform: "translateY(-30px)" }
                        }
                    }}
                />

                <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
                    <Stack spacing={4}>
                        {/* Badge */}
                        <Box
                            sx={{
                                animation: "slideInDown 0.8s ease-out",
                                "@keyframes slideInDown": {
                                    from: { opacity: 0, transform: "translateY(-50px)" },
                                    to: { opacity: 1, transform: "translateY(0)" }
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 2,
                                    py: 0.8,
                                    borderRadius: 50,
                                    background: `linear-gradient(135deg, ${theme.palette.secondary.main}30, ${theme.palette.info.main}30)`,
                                    border: `1px solid ${theme.palette.secondary.main}50`,
                                    backdropFilter: "blur(10px)",
                                    mx: "auto",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        background: theme.palette.secondary.main,
                                        animation: "pulse 2s ease-in-out infinite",
                                        "@keyframes pulse": {
                                            "0%, 100%": { opacity: 1, transform: "scale(1)" },
                                            "50%": { opacity: 0.5, transform: "scale(1.2)" }
                                        }
                                    }}
                                />
                                <Typography
                                    sx={{
                                        color: theme.palette.secondary.main,
                                        fontWeight: 600,
                                        fontSize: { xs: "0.7rem", md: "0.75rem" },
                                        letterSpacing: "0.5px"
                                    }}
                                >
                                    CERTIFICATE VERIFICATION
                                </Typography>
                            </Box>
                        </Box>

                        {/* Main Heading */}
                        <Box
                            sx={{
                                textAlign: "center",
                                animation: "slideInDown 0.8s ease-out 0.2s both",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
                                    fontWeight: 900,
                                    color: "#ffffff",
                                    lineHeight: 1.1,
                                    mb: 1.5,
                                    background: `linear-gradient(135deg, #ffffff 0%, ${theme.palette.secondary.main} 100%)`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            >
                                Verify Your Certificate
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: "0.95rem", md: "1.15rem" },
                                    color: "rgba(255, 255, 255, 0.85)",
                                    lineHeight: 1.7,
                                    fontWeight: 400,
                                    maxWidth: "650px",
                                    mx: "auto"
                                }}
                            >
                                Enter your enrollment number to verify the authenticity of your certificate and access your credentials
                            </Typography>
                        </Box>

                        {/* Search Card */}
                        <Box
                            sx={{
                                animation: "slideInDown 0.8s ease-out 0.4s both",
                            }}
                        >
                            <Card
                                sx={{
                                    background: "rgba(255, 255, 255, 0.1)",
                                    backdropFilter: "blur(20px)",
                                    border: `2px solid ${theme.palette.secondary.main}40`,
                                    borderRadius: 3,
                                    boxShadow: `0 8px 32px ${theme.palette.primary.main}30`,
                                }}
                            >
                                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                    <FormControl fullWidth variant="outlined">
                                        <InputLabel
                                            htmlFor="enrollment-input"
                                            sx={{
                                                color: "rgba(255, 255, 255, 0.7)",
                                                "&.Mui-focused": {
                                                    color: theme.palette.secondary.main
                                                }
                                            }}
                                        >
                                            Enter your enrollment number
                                        </InputLabel>
                                        <OutlinedInput
                                            id="enrollment-input"
                                            type="text"
                                            onChange={(event) => setEnrollment(event.target.value)}
                                            onKeyUp={(event) => event.key === 'Enter' && verify()}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => verify()}
                                                        edge="end"
                                                        sx={{
                                                            color: theme.palette.secondary.main,
                                                            background: `${theme.palette.secondary.main}20`,
                                                            "&:hover": {
                                                                background: `${theme.palette.secondary.main}30`,
                                                                transform: "scale(1.1)"
                                                            },
                                                            transition: "all 0.3s ease"
                                                        }}
                                                    >
                                                        {working ? (
                                                            <CircularProgress
                                                                size={24}
                                                                sx={{ color: theme.palette.secondary.main }}
                                                            />
                                                        ) : (
                                                            <Search />
                                                        )}
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                            sx={{
                                                color: 'white',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                                    borderWidth: 2
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: theme.palette.secondary.main,
                                                },
                                                fontSize: { xs: "1rem", md: "1.1rem" },
                                                py: 1
                                            }}
                                            label="Enter your enrollment number"
                                            placeholder="e.g., SPY2024001"
                                        />
                                    </FormControl>
                                    <Typography
                                        sx={{
                                            mt: 2,
                                            textAlign: 'center',
                                            fontSize: { xs: "0.85rem", md: "0.95rem" },
                                            color: "rgba(255, 255, 255, 0.7)",
                                            lineHeight: 1.6
                                        }}
                                    >
                                        A dynamic learning hub where innovation meets education. Join us to acquire in-demand knowledge and pave the way for a successful career.
                                    </Typography>

                                    <Typography sx={{ mt: 2, textAlign: 'center', fontSize: { xs: "0.85rem", md: "0.95rem" }, color: "rgba(255, 255, 255, 0.7)", lineHeight: 1.6 }}>If you have old issued certificate <a href="https://spywarecomputers.com/v1/student-verification.php" style={{ color: 'wheat' }} >click here</a> to verify   </Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    </Stack>
                </Container>
            </Box>

            {/* Why Verify & Key Benefits Section */}
            <Box
                sx={{
                    position: "relative",
                    py: { xs: 8, md: 12 },
                    background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.primary.main}08 100%)`,
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: "10%",
                        right: "-5%",
                        width: "400px",
                        height: "400px",
                        borderRadius: "50%",
                        background: `${theme.palette.secondary.main}15`,
                        filter: "blur(80px)",
                    },
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: "15%",
                        left: "-5%",
                        width: "350px",
                        height: "350px",
                        borderRadius: "50%",
                        background: `${theme.palette.info.main}15`,
                        filter: "blur(80px)",
                    }
                }}
            >
                <Container maxWidth="lg">
                    <Stack >
                        {/* Section Header */}
                        <Box sx={{ textAlign: "center" }}>
                            <Typography
                                sx={{
                                    fontSize: { xs: "0.85rem", md: "0.95rem" },
                                    color: theme.palette.secondary.main,
                                    fontWeight: 700,
                                    letterSpacing: "2px",
                                    textTransform: "uppercase",
                                    mb: 2,
                                }}
                            >
                                Why Choose Verification
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: "2rem", md: "3rem" },
                                    fontWeight: 900,
                                    color: theme.palette.text.primary,
                                    mb: 2,
                                }}
                            >
                                Trust, Security & Recognition
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: "0.95rem", md: "1.1rem" },
                                    color: theme.palette.text.secondary,
                                    maxWidth: "700px",
                                    mx: "auto",
                                }}
                            >
                                Verify your credentials and unlock a world of opportunities with instant, secure certificate validation
                            </Typography>
                        </Box>

                        {/* Features Grid */}
                        <Grid container spacing={3} mt={5}>
                            {[
                                {
                                    icon: Shield,
                                    title: "Prevent Fraud",
                                    description: "Advanced security measures protect against fake certificates and credential misrepresentation",
                                    iconColor: "#8B5CF6" // Vibrant Purple
                                },
                                {
                                    icon: Verified,
                                    title: "Instant Validation",
                                    description: "Get real-time verification results in seconds with our automated system",
                                    iconColor: "#06B6D4" // Cyan
                                },
                                {
                                    icon: TrendingUp,
                                    title: "Career Growth",
                                    description: "Verified credentials significantly boost your professional credibility and opportunities",
                                    iconColor: "#10B981" // Emerald Green
                                },
                                {
                                    icon: CheckCircle,
                                    title: "Employer Trust",
                                    description: "Employers can instantly verify your qualifications with complete confidence",
                                    iconColor: "#F59E0B" // Amber
                                },
                                {
                                    icon: VerifiedUser,
                                    title: "Digital Access",
                                    description: "Access your verified certificate anytime, anywhere with secure digital storage",
                                    iconColor: "#F43F5E" // Rose/Pink
                                },
                                {
                                    icon: School,
                                    title: "Official Recognition",
                                    description: "Get official recognition from accredited institutions and industry leaders",
                                    iconColor: "#6366F1" // Indigo
                                }
                            ].map((item, index) => {
                                const Icon = item.icon;
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Card
                                            sx={{
                                                height: "100%",
                                                background: "transparent",
                                                borderRadius: 3,
                                                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                                cursor: "pointer",
                                                position: "relative",
                                                overflow: "hidden",
                                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                                                "@media (hover: hover)": {
                                                    "&:hover": {
                                                        transform: "translateY(-8px)",
                                                        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                                                    }
                                                }
                                            }}
                                        >
                                            <CardContent sx={{ p: { xs: 2.5, md: 2.5 }, position: "relative", zIndex: 1, textAlign: "center" }}>
                                                <Stack spacing={1.5} alignItems="center">
                                                    {/* Large Icon */}
                                                    <Box
                                                        sx={{
                                                            width: { xs: 60, md: 70 },
                                                            height: { xs: 60, md: 70 },
                                                            borderRadius: 3,
                                                            background: `linear-gradient(135deg, ${item.iconColor}30, ${item.iconColor}10)`,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            transition: "transform 0.3s ease",
                                                            "&:hover": {
                                                                transform: "rotate(5deg) scale(1.1)",
                                                            }
                                                        }}
                                                    >
                                                        <Icon sx={{ fontSize: "2.2rem", color: item.iconColor }} />
                                                    </Box>

                                                    {/* Content */}
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "1.2rem",
                                                                fontWeight: 700,
                                                                color: theme.palette.text.primary,
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            {item.title}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.85rem",
                                                                color: theme.palette.text.secondary,
                                                                lineHeight: 1.5,
                                                            }}
                                                        >
                                                            {item.description}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Stack>
                </Container>
            </Box>
        </>
    );
}